import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import apiClient from '../services/api/apiClient';

const SelectedAccountContext = createContext();

export const useSelectedAccount = () => {
  const context = useContext(SelectedAccountContext);
  if (!context) {
    throw new Error('useSelectedAccount must be used within SelectedAccountProvider');
  }
  return context;
};

// Helper to get current user ID from token
const getCurrentUserId = () => {
  const token = localStorage.getItem('token');
  if (!token) return null;
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload.userId;
  } catch {
    return null;
  }
};

export const SelectedAccountProvider = ({ children }) => {
  const [accounts, setAccounts] = useState([]);
  const [selectedAccountId, setSelectedAccountId] = useState(() => {
    return localStorage.getItem('selectedAccountId') || null;
  });
  const [isLoading, setIsLoading] = useState(true);
  const [currentUserId, setCurrentUserId] = useState(getCurrentUserId);

  // 載入帳號列表
  const loadAccounts = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setAccounts([]);
        setIsLoading(false);
        return;
      }

      const userId = getCurrentUserId();
      setCurrentUserId(userId);

      const response = await apiClient.get('/user/social-accounts/account-settings');

      if (response.data.success) {
        // API returns { success: true, data: [...] }
        const userAccounts = response.data.data || response.data.accounts || [];

        // API 已經按 user_id 過濾，直接使用
        setAccounts(userAccounts);

        // 檢查當前選中的帳號是否屬於此用戶
        const savedAccountId = localStorage.getItem('selectedAccountId');
        const isValidAccount = userAccounts.some(acc => acc.id === savedAccountId);

        if (userAccounts.length > 0 && !isValidAccount) {
          // 選中的帳號不屬於當前用戶，重新選擇第一個
          const firstAccountId = userAccounts[0].id;
          setSelectedAccountId(firstAccountId);
          localStorage.setItem('selectedAccountId', firstAccountId);
        } else if (userAccounts.length === 0) {
          // 沒有帳號，清除選擇
          setSelectedAccountId(null);
          localStorage.removeItem('selectedAccountId');
        }
      }
    } catch (error) {
      console.error('Failed to load accounts:', error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // 初次載入 + 監聽登入/登出事件
  useEffect(() => {
    loadAccounts();

    // 監聯登入/登出事件（同一 tab 內）
    const handleAuthChange = () => {
      loadAccounts();
    };

    // 監聽 localStorage 變化（用於跨 tab 同步）
    const handleStorageChange = (e) => {
      if (e.key === 'token') {
        loadAccounts();
      }
    };

    window.addEventListener('auth-change', handleAuthChange);
    window.addEventListener('storage', handleStorageChange);

    return () => {
      window.removeEventListener('auth-change', handleAuthChange);
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [loadAccounts]);

  // 當選中帳號變更時，保存到 localStorage
  useEffect(() => {
    if (selectedAccountId) {
      localStorage.setItem('selectedAccountId', selectedAccountId);
    }
  }, [selectedAccountId]);

  // 重新載入帳號列表的方法（直接呼叫 loadAccounts）
  const reloadAccounts = loadAccounts;

  // 獲取當前選中的帳號對象
  const selectedAccount = accounts.find(acc => acc.id === selectedAccountId);

  const value = {
    accounts,
    selectedAccountId,
    setSelectedAccountId,
    selectedAccount,
    isLoading,
    reloadAccounts
  };

  return (
    <SelectedAccountContext.Provider value={value}>
      {children}
    </SelectedAccountContext.Provider>
  );
};
